import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import useOffersUI from 'api/useOffersUI';
import useSegments from 'api/useSegments';
import { type FormikProps, type OfferUI } from 'common/contracts';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import InputSelectDropdown, {
  type InputSelectDropdownOption
} from 'components/InputSelectDropdown/InputSelectDropdown';

import Input from '../../../components/Input/Input';
import RichText from '../../../components/RichText/RichText';
import SegmentSection from '../Sections/SegmentSection';

interface SettingsTabProps {
  formikProps: FormikProps;
  segment: string;
  setSegment: (value: string) => void;
  currentOfferUI?: string;
}

const SettingsTab: React.FC<SettingsTabProps> = ({
  formikProps,
  segment,
  setSegment,
  currentOfferUI
}) => {
  const { publisherId } = useParams();
  const { handleChange, handleBlur, values, touched, errors, setFieldValue } =
    formikProps;
  const { getSegments } = useSegments(publisherId);
  const { getOffersUI } = useOffersUI(publisherId, currentOfferUI);

  const offerDesignOptions = useMemo(() => {
    if (getOffersUI.isLoading || !getOffersUI.data?.result) return [];

    return getOffersUI.data.result
      .filter((oi: OfferUI) => oi.offerUiType === OfferDesignTypes.ProgressBar)
      .map((offerUI: OfferUI) => ({
        label: offerUI.name,
        value: offerUI._id,
        externalId: offerUI.externalId
      }));
  }, [getOffersUI.isLoading, getOffersUI.data?.result]);

  return (
    <div className="w-[528px] mx-auto">
      <RichText
        title="General"
        description="Start with setting up your bar mandatory fields"
        className="mb-5"
      />
      <div className="flex flex-col gap-5 mb-4">
        <div className="grid grid-cols-2 gap-5">
          <Input
            label="Bar External ID"
            placeholder="ID Number"
            type="text"
            name="publisherOfferId"
            value={values.publisherOfferId}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            state={
              touched.publisherOfferId && errors.publisherOfferId
                ? 'error'
                : 'default'
            }
            description={
              touched.publisherOfferId && errors.publisherOfferId
                ? errors.publisherOfferId
                : ''
            }
          />
          <Input
            label="Bar Name"
            placeholder="Name"
            type="text"
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            isRequired
            state={touched.name && errors.name ? 'error' : 'default'}
            description={touched.name && errors.name ? errors.name : ''}
          />
          <InputSelectDropdown
            label="Bar Offer Design"
            buttonDataTestId="progressBarDesignBox"
            listDataTestId="dashProgressBarDesignList"
            isRequired
            key={values.offerUiId}
            defaultSelected={offerDesignOptions.find(
              (option: InputSelectDropdownOption) =>
                option.value === values.offerUiId
            )}
            options={offerDesignOptions}
            onOptionChange={(newValue) => setFieldValue('offerUiId', newValue)}
            placeholder="Select"
          />
        </div>
      </div>
      <SegmentSection
        segment={segment}
        setSegment={setSegment}
        getValues={(field) => values[field]}
        setValue={(field, value, options) =>
          setFieldValue(field, value, options)
        }
        currentSegments={values.segments || []}
        segmentOptions={getSegments.data?.data || []}
      />
    </div>
  );
};

export default SettingsTab;
